<script setup lang="ts">
/**
 * 通用菜单。纯展示 + 键盘导航，**不含任何业务逻辑** ——
 * 业务项由调用方通过 `sections` 传进来（模型见 types/menu.ts）。
 *
 * 几个刻意的设计：
 *
 * 1. **可滚动**。菜单项一多，超出屏幕的那部分就永远点不到了。
 *    `max-height` + `overflow-y: auto`，打开时还会**夹回可见范围**。
 * 2. **挂到 body**。菜单要能盖住画布、不被 `overflow: hidden` 裁掉，所以用 Teleport。
 * 3. **子菜单支持任意层数**。8 个对齐方式平铺会把主菜单撑到需要滚动，
 *    收成「排列 ▸」之后主菜单只剩 9 项。
 *    层数不写死是因为**以后还要在别处接子菜单** ——
 *    只做一层的话那时候又得改一遍。
 *
 * 每一层是**独立的 DOM、独立的 `.np-menu`** —— 不能塞进父层的 DOM，
 * 否则会被父层的 `overflow-y: auto` 裁掉。
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
/*
 * ⚠️ **样式必须 import 进来。**
 *
 * 它原来住在这个文件的 `<style>` 块里（Vue 自动挂上），搬成独立 `.css`
 * 之后**必须显式 import** —— 少了这一行，`.np-menu` 就完全没样式：
 * 菜单落进正常文档流、出现在页面底部、还**把界面顶起来**。
 *
 * ⚠️ 而且当时**没被发现**：`verify:custom` 那类守卫扫的是「源码里有没有
 * 这个类名」，CSS 文件在磁盘上就算数，不检查有没有人 import 它。
 * 现在 `verify:css` 里补了一条「孤儿 CSS」检查（见那个套件）。
 */
import './np-menu.css'
import {
  firstEnabledIndex,
  flattenItems,
  nextEnabledIndex,
  pruneMenu,
  searchMenu,
} from '../types/menu'
import type { MenuItem, MenuSection } from '../types/menu'

const props = defineProps<{
  sections: readonly MenuSection[]
  /** 期望的打开位置（视口坐标）；组件会自己夹回可见范围 */
  x?: number
  y?: number
  /**
   * 顶部显示搜索框。
   *
   * 菜单项一多就**必须**能搜 —— 逐层展开找「条形码」比敲三个字慢得多。
   * 搜索会**穿透所有层级**（见 types/menu.ts 的 searchableEntries）。
   */
  searchable?: boolean
  searchPlaceholder?: string
}>()

const emit = defineEmits<{
  (e: 'select', item: MenuItem): void
  (e: 'close'): void
}>()

const root = ref<HTMLElement | null>(null)
const searchEl = ref<HTMLInputElement | null>(null)
const query = ref('')
const pos = ref({ x: props.x ?? 0, y: props.y ?? 0 })

/* ── 层级栈 ─────────────────────────────────────────────────── */

interface SubLevel {
  /** 扁平项（键盘导航、refs 的下标都按它算） */
  items: MenuItem[]
  /**
   * 原始分组 —— 渲染要它。
   *
   * ⚠️ 不能只留 `items`：`title` / `titleStyle` / `kind` 都是**分组**上的
   * 属性，拍平之后就丢了，于是二级菜单里的分组标题和图标行全都不渲染。
   */
  sections: MenuSection[]
  /** 这一层当前高亮到第几项 */
  index: number
  x: number
  y: number
}

const visibleSections = computed(() =>
  searchMenu(pruneMenu(props.sections), query.value),
)
/** 第 0 层的项。搜索时会被拍成一组，所以是 computed */
const rootItems = computed(() => flattenItems(visibleSections.value))

/**
 * 整个菜单树里有没有可勾选的项 —— 有才留出勾选列。
 *
 * 递归整棵树而不是只看当前层：子菜单里有勾选项、主列表没有时，
 * 只按当前层判断的话子菜单的标题会左右跳。
 */
const hasCheckable = computed(() => {
  // 注意 children 是**分组**列表（MenuSection[]），不是 MenuItem[] —— 递归要对准
  const walk = (sections: readonly MenuSection[]): boolean =>
    sections.some((section) =>
      section.items.some(
        (item) =>
          item.checked !== undefined ||
          (item.children ? walk(item.children) : false),
      ),
    )
  return walk(props.sections)
})

const activeIndex = ref(-1)
/** 第 1 层起的子菜单 */
const stack = ref<SubLevel[]>([])
const subEls = ref<HTMLElement[]>([])

/** 菜单项 DOM 表：`${depth}:${index}` → 元素。不是响应式的，渲染时才需要 */
const itemEls = new Map<string, HTMLElement>()
function setItemEl(d: number, index: number, el: unknown) {
  if (el) itemEls.set(`${d}:${index}`, el as HTMLElement)
}
const itemElAt = (d: number, index: number) => itemEls.get(`${d}:${index}`) ?? null

const depth = computed(() => stack.value.length)
const itemsAt = (d: number): MenuItem[] =>
  d === 0 ? rootItems.value : (stack.value[d - 1]?.items ?? [])
const indexAt = (d: number): number =>
  d === 0 ? activeIndex.value : (stack.value[d - 1]?.index ?? -1)

function setIndexAt(d: number, next: number) {
  if (d === 0) {
    activeIndex.value = next
    return
  }
  const level = stack.value[d - 1]
  if (level) level.index = next
}

/* ── 展开 / 收起 ────────────────────────────────────────────── */

const VIEW_MARGIN = 8
const GAP = 5

/** 摆好某一层：优先在父菜单右侧，放不下翻到左侧，再夹回可见范围 */
function placeLevel(d: number) {
  const el = subEls.value[d - 1]
  const level = stack.value[d - 1]
  if (!el || !level) return

  const rect = el.getBoundingClientRect()
  const parentRect = (d === 1 ? root.value : subEls.value[d - 2])?.getBoundingClientRect()

  let x = (parentRect?.right ?? 0) + GAP
  // 只往右摆的话，菜单贴着屏幕右边时子菜单会整个跑到屏幕外
  if (x + rect.width > window.innerWidth - VIEW_MARGIN) {
    x = Math.max(VIEW_MARGIN, (parentRect?.left ?? 0) - rect.width - GAP)
  }

  level.x = x
  level.y = Math.max(
    VIEW_MARGIN,
    Math.min(level.y, window.innerHeight - rect.height - VIEW_MARGIN),
  )
}

/**
 * 推出下一层子菜单。
 *
 * ⚠️ **必须先 `closeFrom`，这是这个函数唯一的坑**：
 * 鼠标**悬停**会推一层（`onItemEnter` 里清了旧的再推），而**点击**同一项
 * （`onItemClick`）和**回车**（`onKeydown`）原来只调了这个函数、没清旧的
 * —— 于是「悬停推一层 + 点击又推一层」= 屏幕上出现**两份一样的子菜单**。
 *
 * 修法不是在那三个调用点各补一次，而是让**函数自己幂等**：
 * 进来先截断到父层，再推。这样以后新增调用点也不会重犯。
 */
async function pushSubmenu(item: MenuItem, fromDepth: number, index: number) {
  if (item.disabled || !item.children?.length) return
  // 幂等：截断到父层深度（`closeFrom(d)` 保留前 d 层），再推下一层
  closeFrom(fromDepth)

  const anchorRect = itemElAt(fromDepth, index)?.getBoundingClientRect()
  const childItems = flattenItems(item.children)

  stack.value.push({
    items: childItems,
    sections: [...item.children],
    index: firstEnabledIndex(childItems),
    // 先按父项位置估一个，渲染出来之后 placeLevel 会再校正
    x: (anchorRect?.right ?? 0) + GAP,
    y: (anchorRect?.top ?? 0) - 5,
  })

  await nextTick()
  placeLevel(stack.value.length)
}

function closeFrom(d: number) {
  if (stack.value.length > d) stack.value = stack.value.slice(0, d)
}

/* ── 交互 ───────────────────────────────────────────────────── */

function pick(item: MenuItem) {
  if (item.disabled) return
  emit('select', item)
  emit('close')
}

/**
 * 鼠标移到某层的某一项。
 *
 * 先把**比这一层更深的**都收掉 —— 不然鼠标横穿一个有子菜单的项
 * 去点下面的项，那个子菜单会一直挂着。
 */
function onItemEnter(d: number, index: number) {
  closeFrom(d)
  setIndexAt(d, index)
  const item = itemsAt(d)[index]
  if (item?.children?.length) void pushSubmenu(item, d, index)
}

function onItemClick(item: MenuItem, d: number, index: number) {
  if (item.children?.length) {
    void pushSubmenu(item, d, index)
    return
  }
  pick(item)
}

/**
 * 点击菜单外面就关掉。
 *
 * 用 capture 阶段：画布上的 pointerdown 会触发拖动 / 框选，
 * 我们要在它之前先把菜单收掉（但**不**阻止它 —— 用户点节点是有效的操作，
 * 不能因为菜单开着就吞掉）。
 */
function onDocPointerDown(ev: PointerEvent) {
  const target = ev.target as HTMLElement | null
  if (target?.closest('.np-menu')) return
  emit('close')
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') {
    ev.preventDefault()
    ev.stopPropagation()
    // 在子菜单里 → 先退一层；已经在最外层 → 关掉整个菜单
    if (depth.value > 0) closeFrom(depth.value - 1)
    else emit('close')
    return
  }

  if (ev.key === 'ArrowRight') {
    const item = itemsAt(depth.value)[indexAt(depth.value)]
    if (item?.children?.length) {
      ev.preventDefault()
      ev.stopPropagation()
      void pushSubmenu(item, depth.value, indexAt(depth.value))
    }
    return
  }

  if (ev.key === 'ArrowLeft') {
    if (depth.value > 0) {
      ev.preventDefault()
      ev.stopPropagation()
      closeFrom(depth.value - 1)
    }
    return
  }

  if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
    ev.preventDefault()
    ev.stopPropagation()
    const step = ev.key === 'ArrowDown' ? 1 : -1
    setIndexAt(
      depth.value,
      nextEnabledIndex(itemsAt(depth.value), indexAt(depth.value), step),
    )
    return
  }

  /*
   * ⚠️ 搜索框里按空格是在打字，不能当成「执行当前项」。
   * 不区分的话搜索框里根本打不出空格。
   */
  const typing = (ev.target as HTMLElement | null)?.tagName === 'INPUT'

  if (ev.key === 'Enter' || (ev.key === ' ' && !typing)) {
    const d = depth.value
    const item = itemsAt(d)[indexAt(d)]
    if (!item) return
    ev.preventDefault()
    ev.stopPropagation()
    // 有子菜单的项，「回车」是**展开**而不是执行 —— 它没有 run
    if (item.children?.length) void pushSubmenu(item, d, indexAt(d))
    else pick(item)
  }
}

/* ── 位置 ───────────────────────────────────────────────────── */

/** 打开位置夹回可见范围 —— 否则贴着屏幕右下角打开的菜单会有一半在屏幕外 */
function clampIntoView() {
  const el = root.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  pos.value = {
    x: Math.max(
      VIEW_MARGIN,
      Math.min(props.x ?? 0, window.innerWidth - rect.width - VIEW_MARGIN),
    ),
    y: Math.max(
      VIEW_MARGIN,
      Math.min(props.y ?? 0, window.innerHeight - rect.height - VIEW_MARGIN),
    ),
  }
}

// 高亮项跟着键盘走：滚出可视区就拉回来
watch([activeIndex, () => stack.value.length], async () => {
  await nextTick()
  itemElAt(depth.value, indexAt(depth.value))?.scrollIntoView({ block: 'nearest' })
})

// 换关键词后高亮要回到第一项，否则会停在一个已经不存在的位置上
watch(query, () => {
  closeFrom(0)
  activeIndex.value = firstEnabledIndex(rootItems.value)
})

/**
 * 是不是**真·触屏设备**（手机 / 平板）。
 *
 * 用 `matchMedia` 而不是在事件里判断：`onMounted` 时手上没有 pointer
 * 事件，只能问环境。
 *
 * ⚠️ 判据是 `(hover: none) and (pointer: coarse)`，**不是** `(pointer: coarse)`：
 * 这里要决定的是「要不要自动弹出软键盘」—— 那取决于**设备形态**
 * （有没有物理键盘），而不是指针精度。触屏笔记本不该被当成手机。
 */
function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(hover: none) and (pointer: coarse)').matches === true
  )
}

onMounted(async () => {
  await nextTick()
  clampIntoView()
  activeIndex.value = firstEnabledIndex(rootItems.value)
  /*
   * 可搜索的菜单一打开就聚焦输入框 —— 鼠标端少一次点击。
   *
   * ⚠️ **触摸端不这么做**：一聚焦就弹出系统输入法，把半屏挡住，
   * 而手机上的菜单（长按空白、拖线松手）本来就不该顺手带出键盘。
   * 触摸端改成「点了搜索框才聚焦」—— 输入框自己仍可点。
   */
  if (props.searchable && !isTouchDevice()) searchEl.value?.focus()
  // capture 阶段：抢在画布的快捷键之前处理方向键 / Esc
  window.addEventListener('keydown', onKeydown, true)
  document.addEventListener('pointerdown', onDocPointerDown, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown, true)
  document.removeEventListener('pointerdown', onDocPointerDown, true)
})
</script>

<template>
  <Teleport to="body">
    <div
      ref="root"
      class="np-menu"
      role="menu"
      :style="{ left: `${pos.x}px`, top: `${pos.y}px` }"
      @pointerdown.stop
      @contextmenu.prevent
    >
      <div v-if="props.searchable" class="np-menu__search">
        <input
          ref="searchEl"
          v-model="query"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :placeholder="props.searchPlaceholder ?? '搜索…'"
        />
      </div>

      <div v-if="rootItems.length === 0" class="np-menu__empty">没有匹配项</div>

      <template v-for="section in visibleSections" :key="section.id">
        <!-- 「分割组」标题：两侧长横线 + 文字居中 -->
        <div
          v-if="section.title && section.titleStyle === 'divider'"
          class="np-menu__divider-label"
        >
          {{ section.title }}
        </div>
        <div v-else-if="section.title" class="np-menu__title">{{ section.title }}</div>

        <!--
          图标行：横排方按钮。
          ⚠️ 每个按钮都必须带 `title` + `aria-label`（下面是图标本身没有语义）——
          只看图标猜不出「这个箭头是撤销还是重做」，鼠标悬停和读屏都得有答案。
        -->
        <div v-if="section.kind === 'toolbar'" class="np-menu__toolbar">
          <button
            v-for="item in section.items"
            :key="item.id"
            :ref="(el) => setItemEl(0, rootItems.indexOf(item), el)"
            class="np-menu__icon"
            :class="{
              'is-danger': item.danger,
              'is-active': rootItems[activeIndex]?.id === item.id,
            }"
            type="button"
            role="menuitem"
            :title="item.hint ? `${item.label}（${item.hint}）` : item.label"
            :aria-label="item.label"
            :disabled="item.disabled"
            @click="onItemClick(item, 0, rootItems.indexOf(item))"
            @pointerenter="onItemEnter(0, rootItems.indexOf(item))"
          >
            <svg
              v-if="item.icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              v-html="item.icon"
            />
            <span v-else class="np-menu__icon-text">{{ item.label }}</span>
          </button>
        </div>

        <button
          v-else
          v-for="(item, index) in section.items"
          :key="item.id"
          :ref="(el) => setItemEl(0, rootItems.indexOf(item), el)"
          class="np-menu__item"
          :data-menu-id="item.id"
          :class="{
            'is-danger': item.danger,
            'is-active': rootItems[activeIndex]?.id === item.id,
          }"
          type="button"
          role="menuitem"
          :disabled="item.disabled"
          @click="onItemClick(item, 0, rootItems.indexOf(item))"
          @pointerenter="onItemEnter(0, rootItems.indexOf(item))"
        >
          <!-- 勾选列固定宽度，没勾也占位，标题才不会左右跳 -->
        <span v-if="hasCheckable" class="np-menu__check">{{
          item.checked ? '✓' : ''
        }}</span>
        <svg
          v-if="item.icon"
          class="np-menu__leading"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          v-html="item.icon"
        />
        <span class="np-menu__label">{{ item.label }}</span>
          <span v-if="item.hint" class="np-menu__hint">{{ item.hint }}</span>
          <span v-if="item.children?.length" class="np-menu__arrow" aria-hidden="true">
            <svg viewBox="0 0 8 10" fill="none">
              <path
                d="M2 1 L6.4 5 L2 9"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>
      </template>
    </div>

    <!--
      子菜单：每一层都是独立的 .np-menu。
      塞进父层 DOM 的话会被父层的 overflow-y: auto 裁掉。
    -->
    <div
      v-for="(level, d) in stack"
      :key="`sub-${d}`"
      :ref="(el) => { if (el) subEls[d] = el as HTMLElement }"
      class="np-menu np-menu--sub"
      role="menu"
      :style="{ left: `${level.x}px`, top: `${level.y}px` }"
      @pointerdown.stop
      @contextmenu.prevent
    >
      <!--
        ⚠️ 子菜单的渲染和顶层**用同一套结构**（分组标题 / 图标行 / 列表项）。
        第一版这里只渲染了 `level.items` 平铺的按钮，于是二级菜单里
        `title` + `titleStyle: 'divider'` 和 `kind: 'toolbar'` **全都不生效** ——
        「视图 → 布局」的分割组标题就是这么丢的（实测：二级菜单 10 项都在，
        但两个分组标题一个都没渲染）。
      -->
      <template v-for="section in level.sections" :key="section.id">
        <div
          v-if="section.title && section.titleStyle === 'divider'"
          class="np-menu__divider-label"
        >
          {{ section.title }}
        </div>
        <div v-else-if="section.title" class="np-menu__title">{{ section.title }}</div>

        <div v-if="section.kind === 'toolbar'" class="np-menu__toolbar">
          <button
            v-for="item in section.items"
            :key="item.id"
            :ref="(el) => setItemEl(d + 1, level.items.indexOf(item), el)"
            class="np-menu__icon"
            :class="{
              'is-danger': item.danger,
              'is-active': level.index === level.items.indexOf(item),
            }"
            type="button"
            role="menuitem"
            :title="item.hint ? `${item.label}（${item.hint}）` : item.label"
            :aria-label="item.label"
            :disabled="item.disabled"
            @click="onItemClick(item, d + 1, level.items.indexOf(item))"
            @pointerenter="onItemEnter(d + 1, level.items.indexOf(item))"
          >
            <svg
              v-if="item.icon"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              v-html="item.icon"
            />
            <span v-else class="np-menu__icon-text">{{ item.label }}</span>
          </button>
        </div>

        <button
          v-else
          v-for="item in section.items"
          :key="item.id"
          :ref="(el) => setItemEl(d + 1, level.items.indexOf(item), el)"
          class="np-menu__item"
          :data-menu-id="item.id"
          :class="{
            'is-danger': item.danger,
            'is-active': level.index === level.items.indexOf(item),
          }"
          type="button"
          role="menuitem"
          :disabled="item.disabled"
          @click="onItemClick(item, d + 1, level.items.indexOf(item))"
          @pointerenter="onItemEnter(d + 1, level.items.indexOf(item))"
        >
          <span v-if="hasCheckable" class="np-menu__check">{{
            item.checked ? '✓' : ''
          }}</span>
          <svg
            v-if="item.icon"
            class="np-menu__leading"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            v-html="item.icon"
          />
          <span class="np-menu__label">{{ item.label }}</span>
          <span v-if="item.hint" class="np-menu__hint">{{ item.hint }}</span>
          <span v-if="item.children?.length" class="np-menu__arrow" aria-hidden="true">
            <svg viewBox="0 0 8 10" fill="none">
              <path
                d="M2 1 L6.4 5 L2 9"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>
      </template>
    </div>
  </Teleport>
</template>

<!--
  ⚠️ 这里原来有一整块样式（345 行）—— **已抽到 `np-menu.css`**。
  它本来就是非 scoped 的（`.np-menu` 是 Teleport 到 body 的浮层，
  `:root.np-theme-light` 是根级覆盖），所以搬成普通 CSS 文件不改语义。
-->
